import React, { useEffect, useState } from 'react'
import { Card, Table, Button, Select, Form, Row, Col, Divider, DatePicker, Input, InputNumber, message } from 'antd'
import useAssessments from '../hooks/useAssessments'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'

const { Option } = Select
const { TextArea } = Input

const FunctionalAssessments = ({ value = [], onChange }) => {
  const [form] = Form.useForm()
  const { assessments, loading } = useAssessments()
  const [selectedAssessments, setSelectedAssessments] = useState(value)

  useEffect(() => {
    if (value && value.length > 0) {
      setSelectedAssessments(value)
    }
  }, [value])

  const handleAddAssessment = () => {
    form.validateFields().then((values) => {
      const selectedAssessment = assessments.find((a) => a.id === values.assessmentId)

      const newAssessment = {
        id: values.assessmentId,
        name: selectedAssessment?.name || '',
        description: selectedAssessment?.description || '',
        scheduledDate: values.scheduledDate,
        baseline: values.baseline,
        target: values.target,
        notes: values.notes,
        completed: false,
        result: null,
        category: selectedAssessment?.category || ''
      }

      const updatedAssessments = [...selectedAssessments, newAssessment]
      setSelectedAssessments(updatedAssessments)
      onChange(updatedAssessments)

      form.resetFields()
      message.success('Assessment added successfully')
    })
  }

  const handleRemoveAssessment = (assessmentId) => {
    const updatedAssessments = selectedAssessments.filter((a) => a.id !== assessmentId)
    setSelectedAssessments(updatedAssessments)
    onChange(updatedAssessments)
    message.success('Assessment removed successfully')
  }

  const columns = [
    {
      title: 'Assessment',
      dataIndex: 'name',
      key: 'name',
      width: '15%'
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: '12%'
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: '20%'
    },
    {
      title: 'Scheduled Date',
      dataIndex: 'scheduledDate',
      key: 'scheduledDate',
      width: '12%',
      render: (date) => (date ? date.format('DD/MM/YYYY') : '')
    },
    {
      title: 'Baseline',
      dataIndex: 'baseline',
      key: 'baseline',
      width: '8%'
    },
    {
      title: 'Target',
      dataIndex: 'target',
      key: 'target',
      width: '8%'
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      width: '15%'
    },
    {
      title: 'Status',
      key: 'completed',
      width: '8%',
      render: (_, record) => (record.completed ? 'Completed' : 'Pending')
    },
    {
      title: 'Action',
      key: 'action',
      width: '6%',
      render: (_, record) => (
        <Button icon={<DeleteOutlined />} danger type='link' onClick={() => handleRemoveAssessment(record.id)} />
      )
    }
  ]

  return (
    <Card title='Functional Assessments' bordered={false}>
      <Form form={form} layout='vertical'>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name='assessmentId'
              label='Assessment'
              rules={[{ required: true, message: 'Please select an assessment' }]}
            >
              <Select placeholder='Select assessment' loading={loading} showSearch optionFilterProp='children'>
                {assessments.map((assessment) => (
                  <Option key={assessment.id} value={assessment.id}>
                    {assessment.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              name='scheduledDate'
              label='Scheduled Date'
              rules={[{ required: true, message: 'Please select a date' }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={3}>
            <Form.Item name='baseline' label='Baseline' rules={[{ required: true, message: 'Please enter baseline' }]}>
              <InputNumber min={0} max={100} placeholder='Baseline' style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={3}>
            <Form.Item name='target' label='Target' rules={[{ required: true, message: 'Please enter target' }]}>
              <InputNumber min={0} max={100} placeholder='Target' style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name='notes' label='Notes'>
              <Select placeholder='Select notes'>
                <Option value='Assess before therapy session'>Assess before therapy session</Option>
                <Option value='Assess after therapy session'>Assess after therapy session</Option>
                <Option value='Compare with previous results'>Compare with previous results</Option>
                <Option value='Use assistive device if needed'>Use assistive device if needed</Option>
                <Option value="Perform at patient's own pace">Perform at patient's own pace</Option>
                <Option value='Monitor for pain during assessment'>Monitor for pain during assessment</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24} style={{ textAlign: 'right' }}>
            <Button type='primary' onClick={handleAddAssessment} icon={<PlusOutlined />}>
              Add Assessment
            </Button>
          </Col>
        </Row>
      </Form>

      <Divider />

      <Table columns={columns} dataSource={selectedAssessments} rowKey='id' pagination={false} scroll={{ y: 300 }} />

      {selectedAssessments.length === 0 && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          No assessments added yet. Use the form above to add functional assessments to the rehabilitation plan.
        </div>
      )}
    </Card>
  )
}

export default FunctionalAssessments
